import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LifeBuoy, Loader2, MessageCircle, Plus, ChevronRight, Bug,
  HelpCircle, Lightbulb, CreditCard, ArrowLeft,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { useAuth } from '@/context/AuthContext';

const STATUS_LABELS = {
  open: { label: 'Open', cls: 'bg-[#EFF6FF] border-[#2563FF]/40 text-[#3B82F6]' },
  in_progress: { label: 'In progress', cls: 'bg-yellow-950/40 border-yellow-500/40 text-yellow-300' },
  closed: { label: 'Closed', cls: 'bg-gray-900/40 border-gray-600/40 text-[#64748B]' },
};

const CATEGORY_ICON = {
  bug: Bug,
  question: HelpCircle,
  feature: Lightbulb,
  billing: CreditCard,
  other: HelpCircle,
};

function fmt(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function MyTicketsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchTickets = async () => {
      try {
        const { data } = await axios.get(`${API}/support/my-tickets`);
        if (!cancelled) setTickets(data.tickets || []);
      } catch (err) {
        if (!cancelled) toast.error('Failed to load your tickets');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchTickets();
    return () => { cancelled = true; };
  }, []);

  const open = (ticket) => {
    // Use the signed portal URL the backend generated; strip origin so we
    // stay inside the SPA (avoids a full reload).
    try {
      const u = new URL(ticket.portal_url);
      navigate(`${u.pathname}${u.search}`);
    } catch {
      window.location.href = ticket.portal_url;
    }
  };

  const counts = tickets.reduce(
    (acc, t) => ({ ...acc, [t.status]: (acc[t.status] || 0) + 1 }),
    { open: 0, in_progress: 0, closed: 0 },
  );

  return (
    <div className="min-h-screen bg-[#F5F7FB] text-[#0F172A]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <Link
          to="/profile"
          className="inline-flex items-center gap-1.5 font-mono text-[11px] text-[#64748B] hover:text-[#0F172A] uppercase tracking-wider mb-6"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to profile
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 border border-[#E2E8F0] bg-[#F1F5F9]">
            <LifeBuoy className="w-3.5 h-3.5 text-[#2563FF]" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#2563FF]">
              My Tickets
            </span>
          </div>
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <h1
                className="font-heading text-3xl sm:text-4xl text-[#0F172A] tracking-tight"
                data-testid="my-tickets-title"
              >
                Your support history
              </h1>
              <p className="font-body text-sm text-[#64748B] mt-2">
                Open: <span className="text-[#0F172A]">{counts.open}</span> ·
                In progress: <span className="text-[#0F172A]">{counts.in_progress}</span> ·
                Closed: <span className="text-[#0F172A]">{counts.closed}</span>
              </p>
            </div>
            <Link
              to="/support?tab=ticket"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#2563FF] hover:bg-[#2563FF] text-[#0F172A] text-xs font-heading uppercase tracking-widest"
              data-testid="my-tickets-new"
            >
              <Plus className="w-3.5 h-3.5" />
              New ticket
            </Link>
          </div>
        </motion.div>

        {loading ? (
          <div className="py-16 text-center">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-[#2563FF]" />
          </div>
        ) : tickets.length === 0 ? (
          <div
            className="border border-[#E2E8F0] bg-white p-10 text-center"
            data-testid="my-tickets-empty"
          >
            <MessageCircle className="w-12 h-12 mx-auto mb-3 text-[#1E3A8A]" />
            <p className="font-heading text-lg text-[#0F172A] mb-2">
              No tickets yet
            </p>
            <p className="font-body text-sm text-[#64748B] mb-5">
              Need help with something? Open a ticket and the CRATELAB team will get back to you.
            </p>
            <Link
              to="/support?tab=ticket"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#2563FF] hover:bg-[#2563FF] text-[#0F172A] text-xs font-heading uppercase tracking-widest"
            >
              <Plus className="w-3.5 h-3.5" />
              Open your first ticket
            </Link>
          </div>
        ) : (
          <div
            className="border border-[#E2E8F0] bg-white divide-y divide-[#E2E8F0]"
            data-testid="my-tickets-list"
          >
            {tickets.map((t) => {
              const Icon = CATEGORY_ICON[t.category] || HelpCircle;
              const sLabel = STATUS_LABELS[t.status] || STATUS_LABELS.open;
              const lastFromAdmin = t.last_message_role === 'admin';
              return (
                <button
                  key={t.id}
                  onClick={() => open(t)}
                  className="w-full text-left p-5 flex items-start gap-4 hover:bg-[#F1F5F9] transition-colors"
                  data-testid={`my-ticket-row-${t.id}`}
                >
                  <Icon className="w-4 h-4 text-[#2563FF] mt-1 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading text-base text-[#0F172A] truncate">
                        {t.subject}
                      </span>
                      <span
                        className={`shrink-0 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border ${sLabel.cls}`}
                      >
                        {sLabel.label}
                      </span>
                      {lastFromAdmin && t.status !== 'closed' && (
                        <span className="shrink-0 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-[#2563FF] text-[#0F172A]">
                          New reply
                        </span>
                      )}
                    </div>
                    {t.last_message_preview && (
                      <p className="font-body text-sm text-[#64748B] mt-1.5 line-clamp-2">
                        <span className="text-[#64748B] font-mono text-[11px] uppercase tracking-wider mr-1.5">
                          {lastFromAdmin ? 'Team:' : 'You:'}
                        </span>
                        {t.last_message_preview}
                      </p>
                    )}
                    <p className="font-mono text-[11px] text-[#94A3B8] mt-2">
                      {t.message_count} message{t.message_count === 1 ? '' : 's'} · updated {fmt(t.updated_at)}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#475569] mt-1 shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
